/**
 * Checkout Return-to-Store Retention Popup Controller
 * Intercepts users returning from uncompleted checkout, prompts 15% discount,
 * and auto-redirects with discount applied back to checkout.
 * Supports standard cart checkout, Shop Pay (shop.app) direct checkout, and BFCache navigation.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'lacemade:checkout_retention_shown';
  var SESSION_CHECKOUT_KEY = 'lacemade:went_to_checkout';
  var LOCAL_CHECKOUT_KEY = 'lacemade:last_checkout_timestamp';
  var VARIANT_STORAGE_KEY = 'lacemade:last_checkout_variant';
  var COOKIE_CHECKOUT_NAME = 'lacemade_went_to_checkout';
  var DISCOUNT_CODE = 'LACEMADE15';

  function getCookie(name) {
    try {
      var value = '; ' + document.cookie;
      var parts = value.split('; ' + name + '=');
      if (parts.length === 2) return parts.pop().split(';').shift();
    } catch (e) {}
    return null;
  }

  function clearCheckoutSignals() {
    try {
      sessionStorage.removeItem(SESSION_CHECKOUT_KEY);
      localStorage.removeItem(LOCAL_CHECKOUT_KEY);
      document.cookie = COOKIE_CHECKOUT_NAME + '=; path=/; max-age=0; SameSite=Lax';
    } catch (e) {}
  }

  // 1. Safe intent recording (Standard bubbling phase only, never interferes with native payment buttons)
  function recordCheckoutIntent(isDirectCheckout, target) {
    try {
      var now = Date.now();
      sessionStorage.setItem(SESSION_CHECKOUT_KEY, JSON.stringify({ time: now }));
      localStorage.setItem(LOCAL_CHECKOUT_KEY, now.toString());
      document.cookie = COOKIE_CHECKOUT_NAME + '=1; path=/; max-age=3600; SameSite=Lax';

      // Capture product variant ID ONLY for direct checkout (Shop Pay / Buy It Now)
      if (isDirectCheckout && target) {
        var form = target.closest('form[action*="/cart/add"], .product-form');
        if (form) {
          var variantInput = form.querySelector('[name="id"]');
          var qtyInput = form.querySelector('[name="quantity"]');
          var qty = (qtyInput && parseInt(qtyInput.value, 10)) || 1;
          if (variantInput && variantInput.value) {
            var directData = JSON.stringify({
              variantId: variantInput.value,
              quantity: qty,
              time: now
            });
            sessionStorage.setItem(VARIANT_STORAGE_KEY, directData);
            localStorage.setItem(VARIANT_STORAGE_KEY, directData);
          }
        }
      } else {
        // Standard cart checkout: Actively wipe any residual direct-checkout variant cache!
        try {
          sessionStorage.removeItem(VARIANT_STORAGE_KEY);
          localStorage.removeItem(VARIANT_STORAGE_KEY);
        } catch (e) {}
      }
    } catch (err) {}
  }

  // Listen to checkout triggers (Cart checkout, Drawer checkout, Dynamic checkout, Shop Pay)
  document.addEventListener('click', function (e) {
    try {
      var target = e.target;
      if (!target || typeof target.closest !== 'function') return;

      // Ignore normal add to cart actions
      if (target.closest('button[name="add"], input[name="add"], .add-to-cart, [data-add-to-cart]')) {
        return;
      }

      var isDirectCheckout = !!target.closest(
        '.shopify-payment-button, ' +
        '.shopify-payment-button__button, ' +
        'shop-pay-button, ' +
        '[data-shopify="payment-button"], ' +
        '[data-testid*="shop-pay"], ' +
        '[data-testid*="shoppay"]'
      );

      var isStandardCheckout = target.closest(
        'a[href*="/checkout"], ' +
        'button[name="checkout"], ' +
        'input[name="checkout"], ' +
        '[data-checkout], ' +
        '.checkout__button, ' +
        '.additional-checkout-buttons, ' +
        'button[name="goto_pp"], ' +
        'button[name="goto_gc"], ' +
        'form[action*="/cart"] button[name="checkout"]'
      );

      if (isDirectCheckout) {
        recordCheckoutIntent(true, target);
      } else if (isStandardCheckout) {
        recordCheckoutIntent(false, null);
      }
    } catch (err) {}
  }, false);

  // Safe standard cart form submit listener
  document.addEventListener('submit', function (e) {
    try {
      var form = e.target;
      if (!form) return;

      var action = '';
      if (typeof form.action === 'string') {
        action = form.action.toLowerCase();
      } else if (form.getAttribute) {
        action = (form.getAttribute('action') || '').toLowerCase();
      }

      // Ignore add-to-cart, updates, or third party form actions
      if (action.indexOf('/cart/add') !== -1 ||
          action.indexOf('/cart/update') !== -1 ||
          action.indexOf('/cart/change') !== -1 ||
          action.indexOf('/cart/clear') !== -1) {
        return;
      }

      var submitter = e.submitter;
      var submitterName = submitter && submitter.name ? submitter.name.toLowerCase() : '';

      if (submitterName === 'checkout' || (action.indexOf('/checkout') !== -1 && action.indexOf('/cart/add') === -1)) {
        recordCheckoutIntent(false, null);
      }
    } catch (err) {}
  }, false);

  // 2. Dynamic Image Lazy Loader (Zero bandwidth impact on initial page load)
  function loadRetentionImages(dialog) {
    try {
      if (!dialog) return;
      var picture = dialog.querySelector('#lm-retention-picture');
      if (!picture) return;

      var source = picture.querySelector('source[data-srcset]');
      if (source && !source.srcset) {
        var srcset = source.getAttribute('data-srcset');
        if (srcset) source.srcset = srcset;
      }

      var img = picture.querySelector('img[data-src]');
      if (img && !img.src) {
        var src = img.getAttribute('data-src');
        if (src) img.src = src;
      }
    } catch (err) {}
  }

  // 3. Initialize check on both DOMContentLoaded and pageshow (BFCache / browser Back button)
  function initRetention() {
    try {
      var dialog = document.getElementById('lm-retention-dialog');
      if (!dialog || dialog.open) return;

      var urlParams = new URLSearchParams(window.location.search);

      // Testing helper: ?reset_retention=1 clears the frequency lock immediately
      if (urlParams.has('reset_retention')) {
        try {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(VARIANT_STORAGE_KEY);
          sessionStorage.removeItem(VARIANT_STORAGE_KEY);
          clearCheckoutSignals();
          console.log('[LaceMade Retention] Reset frequency lock and checkout signals.');
        } catch (e) {}
      }

      var isTestQuery = urlParams.has('test_retention') || urlParams.has('preview_retention');

      // Direct manual test override via URL parameter (?test_retention=1)
      if (isTestQuery) {
        showRetentionModal(dialog, true, null);
        return;
      }

      // A0. Check Order Completed Lock (Set by Shopify Customer Event 'checkout_completed')
      try {
        var orderCompletedTime = localStorage.getItem('lacemade:order_completed');
        if (orderCompletedTime) {
          var timeDiff = Date.now() - parseInt(orderCompletedTime, 10);
          if (isNaN(timeDiff) || timeDiff < 7 * 24 * 60 * 60 * 1000) {
            console.log('[LaceMade Retention] 拦截跳过：检测到近期已支付成功记录，不打扰付款客户。');
            return;
          }
        }
      } catch (e) {}

      // A. Strict frequency lock: Show ONLY ONCE per visitor across all pages (including /cart and dev theme)
      try {
        if (localStorage.getItem(STORAGE_KEY)) {
          console.log('[LaceMade Retention] 拦截跳过：该访客已弹出过一次（单次频控锁锁定中）。如需重新测试，请在控制台输入 __lmRetention.reset() 或在网址末尾添加 ?reset_retention=1');
          return;
        }
      } catch (e) {
        return;
      }

      // B. Check Checkout Signal (Session, LocalStorage timestamp within 60 mins, Cookie)
      var now = Date.now();
      var isRecentCheckout = false;
      var checkoutTimestamp = 0;

      try {
        var rawSession = sessionStorage.getItem(SESSION_CHECKOUT_KEY);
        if (rawSession) {
          var s = JSON.parse(rawSession);
          if (s && s.time && (now - s.time < 60 * 60 * 1000)) {
            isRecentCheckout = true;
            checkoutTimestamp = Math.max(checkoutTimestamp, s.time);
          }
        }
      } catch (e) {}

      try {
        var localTime = localStorage.getItem(LOCAL_CHECKOUT_KEY);
        if (localTime) {
          var parsedTime = parseInt(localTime, 10);
          if (!isNaN(parsedTime) && (now - parsedTime < 60 * 60 * 1000)) {
            isRecentCheckout = true;
            checkoutTimestamp = Math.max(checkoutTimestamp, parsedTime);
          }
        }
      } catch (e) {}

      if (getCookie(COOKIE_CHECKOUT_NAME)) {
        isRecentCheckout = true;
      }

      // C. Check Referrer Signal (Covers regular checkout, Shop Pay, and in-page return links)
      var referrer = (document.referrer || '').toLowerCase();
      var isShopPayReturn = referrer.indexOf('shop.app') !== -1;
      var isReferrerFromCheckout = referrer.indexOf('/checkouts/') !== -1 || 
                                   referrer.indexOf('/checkout') !== -1 ||
                                   isShopPayReturn ||
                                   referrer.indexOf('checkout.shopify.com') !== -1 ||
                                   referrer.indexOf('pay.shopify.com') !== -1;

      // Defense 1: Strictly exclude any completed order URLs
      var isPaidOrder = referrer.indexOf('thank_you') !== -1 || 
                        referrer.indexOf('thank-you') !== -1 || 
                        referrer.indexOf('order-status') !== -1 ||
                        referrer.indexOf('/orders/') !== -1;

      if (isPaidOrder) {
        console.log('[LaceMade Retention] 拦截跳过：来源为支付完成或订单详情页。');
        clearCheckoutSignals();
        return;
      }

      // Detect browser Back/Forward navigation
      var isBackNav = false;
      try {
        if (window.performance && window.performance.getEntriesByType) {
          var navEntries = window.performance.getEntriesByType('navigation');
          if (navEntries && navEntries.length > 0) {
            isBackNav = navEntries[0].type === 'back_forward';
          }
        } else if (window.performance && window.performance.navigation) {
          isBackNav = window.performance.navigation.type === 2;
        }
      } catch (e) {}

      // Deterministic Return Evaluation (No arbitrary timer hacks):
      // A genuine abandonment return must satisfy at least one of these deterministic signals:
      // 1. Definite Referrer from checkout or Shop Pay (shop.app, /checkouts/, pay.shopify.com)
      // 2. Browser Back-Forward history navigation after visiting checkout (isBackNav && isRecentCheckout)
      // 3. User specifically navigated to the standalone /cart page with a checkout session
      var isCartPage = window.location.pathname.indexOf('/cart') !== -1;
      var isCartReturn = isCartPage && isRecentCheckout;
      var isBackReturn = isBackNav && isRecentCheckout;
      var isActualReturn = isReferrerFromCheckout || isCartReturn || isBackReturn;

      if (!isActualReturn) {
        console.log('[LaceMade Retention] 拦截跳过：未检测到真实的结账放弃返回信号。', {
          isRecentCheckout: isRecentCheckout,
          isReferrerFromCheckout: isReferrerFromCheckout,
          isCartReturn: isCartReturn,
          isBackReturn: isBackReturn,
          referrer: document.referrer || '(空)',
          path: window.location.pathname
        });
        return;
      }

      // Defense 2: Cart Status Check via /cart.js
      fetch('/cart.js?v=' + Date.now())
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          var hasItems = cart && typeof cart.item_count === 'number' && cart.item_count > 0;
          
          var directData = null;
          try {
            var rawDirect = localStorage.getItem(VARIANT_STORAGE_KEY) || sessionStorage.getItem(VARIANT_STORAGE_KEY);
            if (rawDirect) {
              directData = JSON.parse(rawDirect);
              if (!directData || !directData.variantId || (Date.now() - directData.time > 60 * 60 * 1000)) {
                directData = null;
              }
            }
          } catch (e) {}

          // Rule 1: Cart has items -> ALWAYS preserve existing cart items and quantities!
          if (hasItems) {
            try {
              localStorage.removeItem(VARIANT_STORAGE_KEY);
              sessionStorage.removeItem(VARIANT_STORAGE_KEY);
            } catch (e) {}

            console.log('[LaceMade Retention] 触发成功：购物车已有商品，弹出挽留弹窗（保留原购物车所有商品与数量）！', {
              item_count: cart.item_count
            });
            showRetentionModal(dialog, false, null);
            clearCheckoutSignals();
            return;
          }

          // Rule 2: Cart is empty -> ONLY trigger if confirmed returning from Shop Pay direct checkout with valid variant
          var isConfirmedDirectReturn = isShopPayReturn || (isRecentCheckout && directData);
          if (isConfirmedDirectReturn && directData && directData.variantId) {
            console.log('[LaceMade Retention] 触发成功：Shop Pay直达结账空车返回，弹出挽留弹窗（恢复直达单品结账）！', directData);
            showRetentionModal(dialog, false, directData);
            clearCheckoutSignals();
            return;
          }

          // Otherwise, empty cart with no direct checkout record -> Skip!
          console.log('[LaceMade Retention] 拦截跳过：购物车已为空且非Shop Pay直达结账返回。');
          clearCheckoutSignals();
        })
        .catch(function () {
          if (isReferrerFromCheckout) {
            console.log('[LaceMade Retention] 触发成功（备用分支）：来源为结算页，弹出挽留弹窗！');
            showRetentionModal(dialog, false, null);
            clearCheckoutSignals();
          }
        });
    } catch (err) {}
  }

  function showRetentionModal(dialog, isTest, directData) {
    try {
      if (!dialog || dialog.open) return;

      loadRetentionImages(dialog);

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }

      if (!isTest) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            shown: true,
            shownAt: new Date().toISOString()
          }));
        } catch (e) {}
      }

      // Bind Close Button
      var closeBtn = dialog.querySelector('[data-close-retention]');
      if (closeBtn && !closeBtn.dataset.bound) {
        closeBtn.dataset.bound = 'true';
        closeBtn.addEventListener('click', function () {
          closeModal(dialog);
        });
      }

      // Bind Continue / Claim Button
      var continueBtn = dialog.querySelector('[data-claim-retention]');
      if (continueBtn && !continueBtn.dataset.bound) {
        continueBtn.dataset.bound = 'true';
        continueBtn.addEventListener('click', function (e) {
          e.preventDefault();
          try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
              navigator.clipboard.writeText(DISCOUNT_CODE);
            }
          } catch (err) {}

          // If and ONLY IF directData was passed (empty cart + confirmed Shop Pay direct checkout)
          if (directData && directData.variantId) {
            var qty = directData.quantity || 1;
            try {
              localStorage.removeItem(VARIANT_STORAGE_KEY);
              sessionStorage.removeItem(VARIANT_STORAGE_KEY);
            } catch (err) {}
            // Use Shopify Cart Permalink to auto-create checkout with the direct variant and quantity
            window.location.href = '/cart/' + encodeURIComponent(directData.variantId) + ':' + qty + '?discount=' + DISCOUNT_CODE;
          } else {
            // Cart has items: ALWAYS preserve existing cart items and quantities!
            try {
              localStorage.removeItem(VARIANT_STORAGE_KEY);
              sessionStorage.removeItem(VARIANT_STORAGE_KEY);
            } catch (err) {}
            window.location.href = '/discount/' + DISCOUNT_CODE + '?redirect=/checkout';
          }
        });
      }

      // Close on clicking backdrop
      if (!dialog.dataset.boundBackdrop) {
        dialog.dataset.boundBackdrop = 'true';
        dialog.addEventListener('click', function (e) {
          if (e.target === dialog) {
            closeModal(dialog);
          }
        });
      }
    } catch (err) {}
  }

  function closeModal(dialog) {
    try {
      if (!dialog) return;
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    } catch (err) {}
  }

  // Debug & Testing Console Helper
  try {
    window.__lmRetention = {
      reset: function () {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(VARIANT_STORAGE_KEY);
        sessionStorage.removeItem(VARIANT_STORAGE_KEY);
        clearCheckoutSignals();
        console.log('%c[LaceMade Retention] 缓存与锁已重置，可以重新测试！', 'color: green; font-weight: bold;');
      },
      status: function () {
        console.table({
          '已弹窗锁定(shown)': !!localStorage.getItem(STORAGE_KEY),
          '支付成功锁定(order_completed)': !!localStorage.getItem('lacemade:order_completed'),
          'Session结算信号': !!sessionStorage.getItem(SESSION_CHECKOUT_KEY),
          'Local结算信号': !!localStorage.getItem(LOCAL_CHECKOUT_KEY),
          'Cookie结算信号': !!getCookie(COOKIE_CHECKOUT_NAME),
          'ShopPay/快捷Variant': localStorage.getItem(VARIANT_STORAGE_KEY) || sessionStorage.getItem(VARIANT_STORAGE_KEY) || '(无)',
          '来源(referrer)': document.referrer || '(空)',
          '当前页面路径': window.location.pathname
        });
      },
      open: function () {
        var d = document.getElementById('lm-retention-dialog');
        if (d) showRetentionModal(d, true, null);
      }
    };
  } catch (e) {}

  // Hook standard page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRetention);
  } else {
    initRetention();
  }

  // Hook BFCache (Back-Forward Cache) for native browser Back button
  window.addEventListener('pageshow', function () {
    initRetention();
  });
})();
